import { faInfoCircle, faList, faTrashAlt, faTrashCan } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemProps,
  ListItemText,
  ListProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { FocusEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { CartItemDescription } from '../quotations/components/cart-item-description';

import { PentairHeading } from '../../../theme/components/pentair-heading';
import { TableCellDelete, TableCellInfo } from '../../../theme/components/table-cells';
import { Cart, CartItem } from '../../../types/operations';
import { useShoppingCart } from './hook/useShoppingcart';
import { CartAmount, ShoppingCartItem } from './shopping-cart-item';

export const ShoppingCartItems = () => {
  const { cart, emptyCart, isEmpty, isLoading } = useShoppingCart();
  const { t } = useTranslation();

  return (
    <>
      <PentairHeading
        variant="h5"
        spacing="dense"
        marginTop={['0px !important', '6 !important', 'initial']}
        icon={faList}
      >
        {t('ecommerce:heading.cartItems', { defaultValue: 'Cart items' })}
      </PentairHeading>
      {/* Show table for big screen */}
      <TableContainer
        sx={{
          display: ['none', 'none', 'initial'],
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('ecommerce:tableHead.quantity', { defaultValue: 'Quantity' })}</TableCell>
              <TableCell>{t('ecommerce:tableHead.product', { defaultValue: 'Product' })}</TableCell>
              <TableCellInfo disabled />
              <TableCellDelete disabled />
            </TableRow>
          </TableHead>
          <TableBody>
            {cart?.items.map((item: any) => (
              <ShoppingCartItem key={item.id} cartItem={item} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Show list for small screens */}
      <ShoppingCartList
        sx={{
          display: ['initial', 'initial', 'none'],
        }}
        cart={cart}
      />
      {isEmpty && (
        <Box sx={{ backgroundColor: '#eeeeee', p: 2, marginTop: 1, textAlign: 'center' }}>
          <Typography>
            {t('ecommerce:emptyTable.cart', {
              defaultValue: 'The cart is empty. Select a valve to request a quotation',
            })}
          </Typography>
        </Box>
      )}
      {/* The exact space between this button and the bottom is 38px in the UI. The stipulated space is 48px. It is 34px now without any extra styles. Adding an extra 14px marginBottom makes it 48px */}
      <Box sx={{ marginTop: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          onClick={() => {
            emptyCart();
          }}
          startIcon={<FontAwesomeIcon icon={faTrashCan} style={{ fontSize: 18 }}></FontAwesomeIcon>}
          variant={'contained'}
          color={'primary'}
          size={'small'}
          sx={{
            textTransform: 'none',
            display: isEmpty ? 'none' : 'flex',
            marginBottom: '14px',
          }}
          disabled={isEmpty || isLoading}
        >
          {t('ecommerce:buttonLabel.emptyCart', { defaultValue: 'Empty cart' })}
        </Button>
      </Box>
    </>
  );
};

type ShoppingCartListProps = ListProps & {
  cart: Cart | undefined;
};

/**
 *
 * This is the list for mobile screens
 */
const ShoppingCartList = ({ cart, ...props }: ShoppingCartListProps) => {
  return (
    <List {...props}>
      {cart?.items.map((item: any) => (
        <ShoppingCartListItem key={item.id} cartItem={item} />
      ))}
      <Divider />
    </List>
  );
};

type ShoppingCartListItemType = ListItemProps & {
  cartItem: CartItem;
};

const ShoppingCartListItem = ({ cartItem }: ShoppingCartListItemType) => {
  const { id, sku } = cartItem;
  const navigate = useNavigate();
  const { locale } = useParams();

  const { removeCartItems, updateCartItem, isLoading, setShoppingCartOpen } = useShoppingCart();
  const [quantity, setQuantity] = useState(cartItem.quantity);

  const updateQuantity = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const value = Number(e.target?.value) || 1;
      setQuantity(value);
      updateCartItem(id, { id: sku }, value);
    },
    [id, sku, updateCartItem]
  );

  return (
    <>
      <Divider sx={{ borderColor: '#DADADA' }} />
      <ListItem alignItems="flex-start" sx={{ paddingBottom: 1 }}>
        <ListItemText
          primary={
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <CartAmount
                disabled={isLoading}
                value={quantity || ''}
                onBlur={updateQuantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
              />

              <Box>
                <IconButton
                  onClick={() => {
                    navigate(`/${locale}/valve/${cartItem.product?.sku}`);
                    setShoppingCartOpen(false);
                  }}
                >
                  <FontAwesomeIcon icon={faInfoCircle} />
                </IconButton>
                <IconButton onClick={() => removeCartItems([cartItem.id])}>
                  <FontAwesomeIcon icon={faTrashAlt} />
                </IconButton>
              </Box>
            </Box>
          }
          secondary={<CartItemDescription cartItem={cartItem} title={cartItem.product?.sku + ' - ' + cartItem.title} />}
        />
      </ListItem>
    </>
  );
};
